import { ProUserApplication } from '@tsin-core/states/applications/applications.model';
import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { AssignInterviewersCohort, AssignModeratorsCohort, RemoveModeratorCohort, SendPhase2Invitations, DeleteProExam, GetProExam, GetSingleProExam, GetSingleProExamByType, SendFinalDecisionEmail, SaveProExam, SetProExamFee, UpdateStatusProExam, RemoveInterviewerCohort, GetSingleSupervisor, SelectProExam, GetProExamsBySession, GetProExamCompletedApplications, GetProExamCompletedApplicationsSilent, SendPhase2FinalDecisionEmail, SendMMIInvitations } from './pro-exam.actions';
import { LoadingState } from '@tsin-core/entities';
import { ProExamService } from '@tsin-core/services/http/pro-exam.service';
import { catchError, of, tap } from 'rxjs';
import { IProExam, IProExamUser } from './pro-exam.models';

export class ProExamStateModel {
  public exams!: IProExam[];
  public examsList!: IProExam[];
  public applicationList!: ProUserApplication[];
  public selectedExam!: IProExam | null;
  public interviewers!: IProExamUser[];
  public moderators!: IProExamUser[];
  public singleSupervisor: IProExamUser | null = null;
  public loading!: LoadingState;
  /** Application numbers the last final-decision email dispatch left out because
   *  the applicant has no usable email address. / Candidatures exclues du dernier
   *  envoi de décisions finales faute d'adresse courriel exploitable. */
  public lastDecisionEmailSkipped?: string[];
  /** Applications the last Phase 2 final-decision dispatch did not mail because
   *  their decision has no decision email (a Conditional Offer, a Referral), each
   *  as "PRO-123 (Conditional Offer)". Empty after a Phase 1 dispatch.
   *  Candidatures non jointes lors du dernier envoi de phase 2 : leur décision n'a
   *  pas de courriel de décision. */
  public lastDecisionEmailNotMailed?: string[];
}

const defaults = {
  exams: [],
  examsList: [],
  applicationList: [] as ProUserApplication[],
  selectedExam: null,
  interviewers: [],
  moderators: [],
  singleSupervisor: null,
  loading: LoadingState.notLoading
};

@State<ProExamStateModel>({
  name: 'proExamState',
  defaults
})

@Injectable()
export class ProExamState {

  proExamService: ProExamService = inject(ProExamService);

  @Selector()
  static getProExams(state: ProExamStateModel) {
    return state.exams;
  }

  @Selector()
  static getProExamsList(state: ProExamStateModel) {
    return state.examsList;
  }


  @Selector()
  static getSingleProExam(state: ProExamStateModel) {
    return state.selectedExam;
  }

  @Selector()
  static getProExamCompletedApplications(state: ProExamStateModel) {
    return state.applicationList;
  }

  @Selector()
  static getSingleProExamModerators(state: ProExamStateModel) {
    return state.moderators;
  }

  @Selector()
  static getSingleProExamInterviewers(state: ProExamStateModel) {
    return state.interviewers;
  }

  @Selector()
  static getSingleSupervisor(state: ProExamStateModel) {
    return state.singleSupervisor;
  }


  @Selector()
  static getLoading(state: ProExamStateModel) {
    return state.loading;
  }

  @Action(GetProExam)
  getProExams({ patchState }: StateContext<ProExamStateModel>, action: GetProExam) {
    patchState({ loading: LoadingState.loadingList });
    return this.proExamService.getAllExams().pipe(
      tap((exams: { data: IProExam[] }) => {
        patchState({ exams: exams.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetProExamsBySession)
  getProExamsBySession({ patchState }: StateContext<ProExamStateModel>, action: GetProExamsBySession) {
    patchState({ loading: LoadingState.loadingList });
    return this.proExamService.getExamsBySession().pipe(
      tap((exams: { data: IProExam[] }) => {
        patchState({ examsList: exams.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(GetProExamCompletedApplications)
  getProExamCompletedApplications({ patchState }: StateContext<ProExamStateModel>, action: GetProExamCompletedApplications) {
    patchState({ loading: LoadingState.loadingList });
    return this.proExamService.getCompletedApplications(action.payload.examId).pipe(
      tap((applications: { data: ProUserApplication[] }) => {
        patchState({ applicationList: applications.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetProExamCompletedApplicationsSilent)
  getProExamCompletedApplicationsSilent({ patchState }: StateContext<ProExamStateModel>, action: GetProExamCompletedApplicationsSilent) {
    return this.proExamService.getCompletedApplications(action.payload.examId).pipe(
      tap((applications: { data: ProUserApplication[] }) => {
        patchState({ applicationList: applications.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetSingleSupervisor)
  getSingleSupervisor({ patchState }: StateContext<ProExamStateModel>, action: GetSingleSupervisor) {
    patchState({ loading: LoadingState.loadingList });
    return this.proExamService.getSingleSupervisor(action.payload).pipe(
      tap((user: IProExamUser) => {
        patchState({ singleSupervisor: user, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(GetSingleProExam)
  getSingleProExam({ patchState }: StateContext<ProExamStateModel>, action: GetSingleProExam) {
    patchState({ loading: LoadingState.loadingList });
    return this.proExamService.getSingleExamsById(action.payload).pipe(
      tap((users: IProExamUser[]) => {
        patchState({ moderators: users.filter((moderator: IProExamUser) => moderator.type.toLowerCase() == 'moderator'), interviewers: users.filter((interviewer: IProExamUser) => interviewer.type.toLowerCase() == 'interviewer'), loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetSingleProExamByType)
  getSingleProExamByType({ patchState }: StateContext<ProExamStateModel>, action: GetSingleProExamByType) {
    patchState({ loading: LoadingState.loadingList });
    return this.proExamService.getSingleExamsByType(action.payload).pipe(
      tap((users: IProExamUser[]) => {
        if (action.payload.type.toLowerCase() == "moderator") {
          patchState({ moderators: users, loading: LoadingState.notLoading });
        }
        if (action.payload.type.toLowerCase() == "interviewer") {
          patchState({ interviewers: users, loading: LoadingState.notLoading });
        }
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SelectProExam)
  selectProExam(ctx: StateContext<ProExamStateModel>, action: SelectProExam) {
    ctx.patchState({ selectedExam: action.payload });
  }

  @Action(AssignModeratorsCohort)
  assignModeratorsCohort(ctx: StateContext<ProExamStateModel>, action: AssignModeratorsCohort) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.proExamService.assignModeratorsCohort(action.payload).pipe(
      tap((result: any) => {
        const state = ctx.getState();
        ctx.patchState({
          moderators: result,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(AssignInterviewersCohort)
  assignInterviewersCohort(ctx: StateContext<ProExamStateModel>, action: AssignInterviewersCohort) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.proExamService.assignInterviewersCohort(action.payload).pipe(
      tap((result: any) => {
        const state = ctx.getState();
        ctx.patchState({
          interviewers: result,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(RemoveInterviewerCohort)
  removeInterviewerCohort(ctx: StateContext<ProExamStateModel>, action: RemoveInterviewerCohort) {
    ctx.patchState({ loading: LoadingState.loadingDelete });
    return this.proExamService.removeInterviewerAssignment(action.payload).pipe(
      tap((result: { data: IProExam }) => {
        // console.log('SET FEE FOR PRO EXAM>>', result.data);
        let interviewers = ctx.getState().interviewers;
        // const index = exams.findIndex((exam) => exam.id === result.data.id);
        // exams[index] = result.data;
        ctx.patchState({
          interviewers: interviewers.filter((interviewer: IProExamUser) => interviewer._id !== action.payload.interviewerId),
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(RemoveModeratorCohort)
  removeModeratorCohort(ctx: StateContext<ProExamStateModel>, action: RemoveModeratorCohort) {
    ctx.patchState({ loading: LoadingState.loadingDelete });
    return this.proExamService.removeModeratorAssignment(action.payload).pipe(
      tap((result: { data: IProExam }) => {
        // console.log('Remove MOderator for PRO EXAM', result.data);
        let moderators = ctx.getState().moderators;
        // const index = exams.findIndex((exam) => exam.id === result.data.id);
        // exams[index] = result.data;
        ctx.patchState({
          moderators: moderators.filter((moderator: IProExamUser) => moderator._id !== action.payload.moderatorId),
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(SaveProExam)
  saveProExam(ctx: StateContext<ProExamStateModel>, action: SaveProExam) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.proExamService.createProExam(action.payload).pipe(
      tap((result: { data: IProExam }) => {
        // console.log('Create PRO EXAM>>', result.data);
        const state = ctx.getState();
        ctx.patchState({
          exams: [...state.exams, result.data],
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(UpdateStatusProExam)
  closeProExam(ctx: StateContext<ProExamStateModel>, action: UpdateStatusProExam) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.proExamService.changeStatus(action.payload.id, action.payload.status).pipe(
      tap((result: { data: IProExam }) => {
        // console.log('UPDATE STATUS PRO EXAM>>', result.data);
        let exams = ctx.getState().exams;
        const index = exams.findIndex((exam) => exam.id === result.data.id);
        exams[index] = result.data;
        ctx.patchState({
          exams,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(SetProExamFee)
  setProExamFee(ctx: StateContext<ProExamStateModel>, action: SetProExamFee) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.proExamService.setExamFee(action.payload.id, action.payload.fee).pipe(
      tap((result: { data: IProExam }) => {
        // console.log('SET FEE FOR PRO EXAM>>', result.data);
        let exams = ctx.getState().exams;
        const index = exams.findIndex((exam) => exam.id === result.data.id);
        exams[index] = result.data;
        ctx.patchState({
          exams,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(DeleteProExam)
  deleteProExam({ patchState }: StateContext<ProExamStateModel>, action: DeleteProExam) {
    patchState({ loading: LoadingState.loadingDelete });
    return this.proExamService.deleteProExam(action.payload).pipe(
      tap((exams: { data: IProExam[] }) => {
        // console.log(exams);
        patchState({ exams: exams.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SendFinalDecisionEmail)
  sendFinalDecisionMade(ctx: StateContext<ProExamStateModel>, action: SendFinalDecisionEmail) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.proExamService.sendFinalDecisionEmail(action.payload).pipe(
      tap((response: any) => {
        // // console.log('Final Decision Email Sent', response);
        // Cleared so a Phase 2 result from an earlier send is not shown against this one.
        ctx.patchState({ loading: LoadingState.notLoading, lastDecisionEmailSkipped: response?.skipped ?? [], lastDecisionEmailNotMailed: [] });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SendPhase2FinalDecisionEmail)
  sendPhase2FinalDecisionEmail(ctx: StateContext<ProExamStateModel>, action: SendPhase2FinalDecisionEmail) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.proExamService.sendPhase2FinalDecisionEmail(action.payload).pipe(
      tap((response: any) => {
        // // console.log('Phase 2 Final Decision Email Sent', response);
        ctx.patchState({ loading: LoadingState.notLoading, lastDecisionEmailSkipped: response?.skipped ?? [], lastDecisionEmailNotMailed: response?.notMailed ?? [] });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SendMMIInvitations)
  sendMMIInvitations(ctx: StateContext<ProExamStateModel>, action: SendMMIInvitations) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.proExamService.sendMMIInvitations(action.payload).pipe(
      tap((response: any) => {
        // // console.log('MMI Invitations Sent', response);
        ctx.patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }



  @Action(SendPhase2Invitations)
  sendPhase2Invitations(ctx: StateContext<ProExamStateModel>, action: SendPhase2Invitations) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.proExamService.sendPhase2Invitations(action.payload).pipe(
      tap((response: any) => {
        // // console.log('Phase 2 Invitations Sent', response);
        ctx.patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );

  }
}
